import React, { useContext, useEffect, useRef, useState } from 'react';
import { ReactReduxContext } from 'react-redux';
import { runContinueAction } from '../continueAt';
import SendGlyph from '../../sendGlyph';
import './StationFace.css';

// Who a face belongs to, as the page knows it: {id, name, image, href, placeholder}; the
// placeholder shows behind the face until it draws, and stays if it never does. The Stations grid provides
// it per tile; in a chat it is the chat's station. Never read from the face payload, which the
// model wrote.
export const FaceIdentity = React.createContext(null);

function chatStation(redux) {
  try {
    const st = redux && redux.store.getState().chat.station;
    const s = st && st.station;
    const id = s && (s.station_id ?? s.id);
    if (id == null) return null;
    return { id, name: s.name || '', image: (s.config && s.config.image_url) || null };
  } catch (e) {
    return null;
  }
}

export function useFaceIdentity(structured) {
  const fromPage = useContext(FaceIdentity);
  const redux = useContext(ReactReduxContext);
  const sid = String((structured && structured.station_id) ?? '');
  if (fromPage && String(fromPage.id) === sid) return { ...fromPage, here: false };
  const chat = chatStation(redux);
  if (chat && String(chat.id) === sid) return { ...chat, here: true };
  return { id: sid, name: '', image: null, here: false };
}

// A face always draws on the same 320x400 canvas, scaled to the poster's width, so it looks the
// same in a chat panel and in a half-width tile.
export const FACE_CANVAS_WIDTH = 320;

function FacePoster({ placeholder, covered, children }) {
  const ref = useRef(null);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof window.ResizeObserver !== 'function') return undefined;
    const ro = new window.ResizeObserver(() => {
      const w = el.clientWidth;
      if (w > 0) setScale(w / FACE_CANVAS_WIDTH);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div className="station-face-poster" ref={ref}>
      {placeholder && !covered && <div className="station-face-placeholder">{placeholder}</div>}
      <div className="station-face-canvas" style={{ transform: `scale(${scale})` }}>{children}</div>
    </div>
  );
}

// The host's frame around every station face: a fixed poster, the station's own name and picture,
// and Open station. Regular cards never get it, so it alone marks a face.
export default function StationFace({ structured, covered = false, children }) {
  const who = useFaceIdentity(structured);
  const draft = Boolean(structured && structured.draft);
  const cont = !draft && !who.here && structured && structured.continue;
  const name = who.name || 'Station face';
  return (
    <figure className="station-face-frame" data-testid="station-face-frame">
      <FacePoster placeholder={who.placeholder} covered={covered}>{children}</FacePoster>
      <figcaption className="station-face-band">
        {who.image && (
          <img key={who.image} className="station-face-avatar" src={who.image} alt=""
               onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        )}
        {who.href ? <a className="station-face-name" href={who.href}>{name}</a>
          : <span className="station-face-name">{name}</span>}
        {draft && <span className="station-face-badge">Preview</span>}
        {cont && (
          <button type="button" className="station-face-open" title={cont.label}
                  aria-label={cont.label} onClick={() => runContinueAction(cont, structured)}>
            <SendGlyph height={22} light label={cont.label} />
          </button>
        )}
      </figcaption>
    </figure>
  );
}
